import { useCallback, useEffect, useRef, useState } from "react";
import "./UserMembers.scss";
import { IUser } from "../../types/api";
import { listUsers, searchUsers } from "../../api/users/users";
import { capFirstAndRemoveDash, capitalizeWords } from "../../shared/utility";
import { toast } from "react-toastify";
import Spinner from "../../components/UI/Spinner/Spinner";

interface IAdminHandleusersProps {}

function AdminHandleUsers(props: IAdminHandleusersProps) {
  const [users, setUsers] = useState<IUser[]>([]);
  const [searchedUsers, setSearchedUsers] = useState<IUser[]>([]);
  const itemsPerPage = 20;
  const [usersNextKey, setUsersNextKey] = useState<string | null>(null);
  const [searchString, setSearchString] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  async function fetchUsers(append = false) {
    const nextStartKey = usersNextKey || "none";
    const limit = itemsPerPage;
    setLoading(true);
    try {
      const response = await listUsers("user", limit, nextStartKey);
      if (response.next_start_key === "none") {
        setUsersNextKey(null);
      } else {
        setUsersNextKey(response.next_start_key);
      }

      setUsers(append ? [...users, ...response.users] : response.users);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchUsers();
  }, []);

  /** Search users */
  // The search runs on every keystroke, so an empty result would otherwise
  // raise one toast per character typed. Same throttle as the admin page.
  const lastWarningTimeRef = useRef<number>(0);
  const showWarning = useCallback(() => {
    const now = Date.now();
    if (now - lastWarningTimeRef.current >= 5000) {
      lastWarningTimeRef.current = now;
      toast.warning("Inga användare hittades");
    }
  }, []);

  const latestUserSearchRef = useRef<string>("");

  const fetchSearchUsers = async (search: string) => {
    latestUserSearchRef.current = search;
    try {
      const response = await searchUsers(search, "user", false, "CONTACTS");
      if (latestUserSearchRef.current !== search) {
        return;
      }
      if (response.length === 0) {
        showWarning();
      }
      setSearchedUsers(response);
    } catch (error: any) {
      toast.error(error);
    }
  };
  useEffect(() => {
    if (searchString.length > 0) {
      handleSearchUsers();
    } else {
      setSearchedUsers([]);
    }
  }, [searchString]);

  const handleSearchUsers = () => {
    fetchSearchUsers(searchString);
  };
  /** End search users  */

  const usersToDisplay = searchString.length > 0 ? searchedUsers : users;
  if (users.length === 0) return null;
  return (
    <div className="user-handle-users-layout">
      <h1 className="text-l primary-text">Medlemmar</h1>
      <div className="handle-users-actions-wrapper">
        <div className="search-input-wrapper">
          <input
            className="users-search-input text-m"
            value={searchString}
            onChange={(e) => setSearchString(e.target.value)}
            placeholder="Sök "
          ></input>
          <i className="fa-regular fa-magnifying-glass search-icon"></i>
        </div>
      </div>
      <table className="users-table-no-click">
        <thead>
          <tr>
            <th className="text-m secondary-text">Medlemsnummer</th>
            <th className="text-m secondary-text">Förnamn</th>
            <th className="text-m secondary-text">Efternamn</th>
            <th className="text-m secondary-text">Medlemskap</th>
            <th className="text-m secondary-text">Telefon</th>
          </tr>
        </thead>
        <tbody>
          {usersToDisplay.map((user, index) => {
            return (
              <tr key={index}>
                <td className="text-m primary-text">{user.member_id}</td>
                <td className="text-m primary-text">
                  {capitalizeWords(user.first_name)}
                </td>
                <td className="text-m primary-text">
                  {capitalizeWords(user.last_name)}
                </td>
                <td className="text-m primary-text">
                  {capFirstAndRemoveDash(user.member_type)}
                </td>

                <td className="text-m primary-text">{user.phone_number}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="users-users-load-more">
        <div className="users-users-spinner-wrapper">
          {loading && <Spinner />}
        </div>

        {usersNextKey && (
          <button
            className="text-m"
            disabled={loading}
            onClick={() => fetchUsers(true)}
          >
            Fler medlemmar
          </button>
        )}
      </div>
    </div>
  );
}

export default AdminHandleUsers;
